import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import makeStyles from '@mui/styles/makeStyles';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRangePicker, DateRange } from 'react-date-range';
import { es } from 'react-date-range/src/locale';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { fingoInputRanges, fingoStaticRanges } from '../../helpers';

const useStyles = makeStyles((theme) => ({
  dayStartPreview: {
    border: 0,
    backgroundColor: theme.palette.primary.A200,
  },
  dayToday: {
    '& .rdrDayNumber span:after': {
      background: theme.palette.primary.main,
    },
  },
  dateRangePicker: {
    '& *': {
      fontFamily: `${theme.typography.fontFamily} !important`,
    },
    '& .rdrStaticRangeLabel, & .rdrInputRangeLabel': {
      fontFamily: `${theme.typography.fontFamily}`,
      fontSize: `${theme.typography.body2.fontSize} !important`,
      fontWeight: `${theme.typography.body2.fontWeight} !important`,
      color: `${theme.palette.text.primary} !important`,
    },
    '& .rdrStaticRangeSelected .rdrStaticRangeLabel, & .rdrInputRangeSelected .rdrInputRangeLabel': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .rdrDayNumber span': {
      fontFamily: `${theme.typography.fontFamily} !important`,
      fontSize: `${theme.typography.body2.fontSize} !important`,
      fontWeight: `${theme.typography.body2.fontWeight} !important`,
    },
    '& .rdrWeekDays': {
      fontFamily: `${theme.typography.fontFamily} !important`,
    },
    '& .rdrWeekDay': {
      fontFamily: `${theme.typography.fontFamily} !important`,
      fontSize: `${theme.typography.body2.fontSize} !important`,
      fontWeight: `${theme.typography.body2.fontWeight} !important`,
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  staticRangeLabels: {
    '& .rdrStaticRange': {
      '& .rdrStaticRangeLabel': {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
        color: theme.palette.text.primary,
        lineHeight: theme.typography.body2.lineHeight,
        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
      '&.rdrStaticRangeSelected .rdrStaticRangeLabel': {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    '& .rdrInputRange': {
      '& .rdrInputRangeLabel': {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
        color: theme.palette.text.primary,
        lineHeight: theme.typography.body2.lineHeight,
        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
      '&.rdrInputRangeSelected .rdrInputRangeLabel': {
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },
}));

const FingoDateRangePicker = ({ classNames, ...props }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(
    theme.breakpoints.down('lg'),
    { defaultMatches: true },
  );
  if (isMobile) {
    return (
      <Box className={classes.dateRangePicker}>
        <DateRange
          locale={es}
          color={theme.palette.primary.main}
          months={1}
          {...props}
          classNames={{
            dayStartPreview: classes.dayStartPreview,
            dayInPreview: classes.dayStartPreview,
            dayEndPreview: classes.dayStartPreview,
            dayToday: classes.dayToday,
            staticRangeLabels: classes.staticRangeLabels,
            ...classNames,
          }}
        />
      </Box>
    );
  }
  return (
    <Box className={classes.dateRangePicker}>
      <DateRangePicker
        locale={es}
        staticRanges={fingoStaticRanges}
        inputRanges={fingoInputRanges}
        color={theme.palette.primary.main}
        months={2}
        {...props}
        classNames={{
          dayStartPreview: classes.dayStartPreview,
          dayInPreview: classes.dayStartPreview,
          dayEndPreview: classes.dayStartPreview,
          dayToday: classes.dayToday,
          staticRangeLabels: classes.staticRangeLabels,
          ...classNames,
        }}
      />
    </Box>
  );
};

FingoDateRangePicker.defaultProps = {
  classNames: {},
};

FingoDateRangePicker.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classNames: PropTypes.object,
};

export default FingoDateRangePicker;
